<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.controls') }}
      </span>
    </v-col>

    <v-row no-gutters class="py-0 px-2">
      <span>
        {{ $t('message.pages.video.messageControls') }}
      </span>
      <v-col cols="12">
        <v-row class="mt-5">
          <v-col cols="6" class="pt-0">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    label="Play/Pause"
                    value="play"
                    hide-details
                    :disabled="localConfig.disablePause || !hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <div class="d-flex flex-column align-center">
                <span
                  >{{ $t('message.pages.video.disabledDisablePauseText1') }}
                </span>
                <span
                  >{{ $t('message.pages.video.disabledDisablePauseText2') }}
                </span>
                <span>{{
                  $t('message.pages.video.disabledDisablePauseText3')
                }}</span>
              </div>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    :label="
                      $t('message.pages.video.controlsPage.progress.label')
                    "
                    value="progress"
                    hide-details
                    :disabled="alternativeProgress || !hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    v-model="localConfig.controls"
                    class="mt-2"
                    value="current-time"
                    hide-details
                    :label="
                      $t('message.pages.video.controlsPage.currentTime.label')
                    "
                    :disabled="!hasEditVideoPerm"
                    @click="changePlayer"
                  />
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    label="Volume"
                    value="volume"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    :label="$t('message.pages.video.controlsPage.captions')"
                    value="captions"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    :label="$t('message.pages.video.controlsPage.settings')"
                    value="settings"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    label="Fullscreen"
                    value="fullscreen"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer"
                    label="Picture-in-Picture"
                    value="pip"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer(false, true)"
                    :label="$t('message.pages.video.controlsPage.forward')"
                    value="fast-forward"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom :disabled="!alternativeProgress">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    class="mt-2"
                    v-model="localConfig.controls"
                    @click="changePlayer(false, true)"
                    :label="$t('message.pages.video.controlsPage.backward')"
                    value="rewind"
                    hide-details
                    :disabled="!hasEditVideoPerm"
                  ></v-switch>
                </div>
              </template>
              <span>{{
                $t('message.pages.video.disabledAlternativeProgress')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-switch
              class="mt-2"
              v-model="localConfig.hideControlsOnStart"
              @click="changePlayer(false, true)"
              :label="$t('message.pages.video.controlsPage.hideControls')"
              hide-details
              :disabled="!hasEditVideoPerm"
            ></v-switch>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-switch
              class="mt-2"
              v-model="localConfig.restartAfterEnd"
              @click="changePlayer(false, true)"
              :label="$t('message.pages.video.controlsPage.restartAfterEnd')"
              hide-details
              :disabled="!hasEditVideoPerm"
            ></v-switch>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    v-model="localConfig.disablePause"
                    class="mt-2"
                    hide-details
                    :label="$t('message.pages.video.controlsPage.disablePause')"
                    :disabled="!hasEditVideoPerm"
                    @click="changePlayer(false, true)"
                  />
                </div>
              </template>
              <span>{{
                $t('message.pages.video.controlsPage.disablePauseDisclaimer')
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-switch
                    v-model="localConfig.disableForward"
                    class="mt-2"
                    :label="
                      $t(
                        'message.pages.video.controlsPage.disableDblClickForward'
                      )
                    "
                    :disabled="!hasEditVideoPerm"
                    hide-details
                    @click="changePlayer(false, true)"
                  />
                </div>
              </template>
              <span>{{
                $t(
                  'message.pages.video.controlsPage.disableDblClickForwardDisclaimer'
                )
              }}</span>
            </v-tooltip>
          </v-col>

          <v-col cols="6" class="pt-0">
            <v-switch
              class="mt-2"
              v-model="localConfig.controls"
              @click="changePlayer(true, true)"
              label="Airplay"
              value="airplay"
              hide-details
              :disabled="!hasEditVideoPerm"
            ></v-switch>
          </v-col>
          <v-col cols="6" class="pt-0">
            <v-switch
              class="mt-2"
              v-model="localConfig.controls"
              @click="changePlayer(true, true)"
              label="Chromecast"
              value="cast"
              hide-details
              :disabled="!hasEditVideoPerm"
            ></v-switch>
          </v-col>
        </v-row>
      </v-col>

      <!-- settings::language -->
      <v-col cols="12" class="mt-5">
        <div class="mb-6 mt-5">
          <h3 class="player-subtitle">
            {{ $t('message.playerPage.language') }}
          </h3>
          <v-select-legacy
            v-model="localConfig.defaultLanguage"
            :items="languages"
            :menu-props="{ top: false, offsetY: true }"
            :label="$t('message.playerPage.languageLabel')"
            class="mt-2"
            item-text="name"
            item-value="value"
            outlined
            dense
            hide-details
            @change="changePlayer"
            :disabled="!hasEditVideoPerm"
          />
        </div>
      </v-col>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addControls') }}
    </span>

    <v-row no-gutters v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-8 pt-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            class="px-6"
            color="primary"
            :disabled="loadingButtonSave || withoutChanges"
            :loading="loadingButtonSave"
            @click="handleUpdateControls"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
// external imports
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

// internal imports
import i18n from '@/translations/lang'

// API

// Components
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'

export default {
  name: 'VideoPlayerControls',
  components: {
    UnsavedChangesModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    videoId: '',
    profileId: null,
    profileConfig: null,
    loadingButtonSave: false,
    videoPlayer: {},
    alternativeProgress: false,
    initialConfig: {
      controls: [],
      hideControlsOnStart: false,
      restartAfterEnd: false,
      disablePause: false,
      disableForward: false,
      defaultLanguage: '',
    },
    localConfig: {
      controls: [],
      hideControlsOnStart: false,
      restartAfterEnd: false,
      disablePause: false,
      disableForward: false,
      defaultLanguage: '',
    },
    withoutChanges: true,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.videoPlayer = this.$store.state.video.player.config
    this.alternativeProgress =
      this.$store.state.video.profile.vsl?.alternativeProgress
    this.localConfig.defaultLanguage =
      this.$store.state.video.profile.vsl?.defaultLanguage || ''
    this.localConfig.controls = this.$store.state.video.profile.controls
      ? this.$store.state.video.profile.controls
      : [
          'play-large',

          'rewind',

          'play',

          'fast-forward',

          'progress',

          'current-time',

          'volume',

          'captions',

          'settings',

          'pip',

          'fullscreen',
        ]
    this.localConfig.hideControlsOnStart =
      this.$store.state.video.profile?.hideControlsOnStart
    this.localConfig.restartAfterEnd =
      this.$store.state.video.profile?.vsl.restartAfterEnd
    this.localConfig.disableForward =
      this.$store.state.video.profile?.vsl.disableForward
    this.localConfig.disablePause =
      this.$store.state.video.profile?.vsl.disablePause

    this.initialConfig = _.cloneDeep(this.localConfig)
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    localConfig: {
      handler() {
        this.withoutChanges = this.checkChanges()

        if (this.localConfig.disablePause) {
          const playControlIndex = this.localConfig.controls.findIndex(
            (control) => control === 'play'
          )

          if (playControlIndex !== -1) {
            const newControls = [...this.localConfig.controls]
            newControls.splice(playControlIndex, 1)

            this.$set(this.localConfig, 'controls', newControls)
            this.localConfig.disablePause = true
            this.changePlayer(false, true)
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    mustReloadToPreview() {
      return (
        !_.isEqual(
          this.profile.hideControlsOnStart,
          this.localConfig.hideControlsOnStart
        ) ||
        (!_.isEqual(
          this.profile.vsl.defaultLanguage,
          this.localConfig.defaultLanguage
        ) &&
          this.localConfig.defaultLanguage !== '') /*||
        this.localConfig.controls.includes('cast') !==
          this.initialConfig.controls.includes('cast') ||
        this.localConfig.controls.includes('airplay') !==
          this.initialConfig.controls.includes('airplay')*/
      )
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    languages() {
      return [
        {
          value: '',
          name: this.$i18next.t('message.automatic'),
        },
        {
          value: 'pt',
          name: this.$i18next.t('message.portuguese'),
        },
        {
          value: 'en',
          name: this.$i18next.t('message.english'),
        },
        {
          value: 'es',
          name: this.$i18next.t('message.spanish'),
        },
      ]
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleUpdateControls() {
      this.loadingButtonSave = true
      this.organizeControls()
      const colors = this.$store.state.video.profile.colors
      let vsl = this.$store.state.video.profile.vsl

      vsl.disableForward = this.localConfig.disableForward
      vsl.disablePause = this.localConfig.disablePause
      vsl.restartAfterEnd = this.localConfig.restartAfterEnd

      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([
          this.profileId,
          {
            controls: this.localConfig.controls,
            hideControlsOnStart: this.localConfig.hideControlsOnStart,
            vsl: vsl,
            colors: colors,
          },
        ])
        this.internalError
          ? ''
          : (this.handleSnackbar(this.$t('message.pages.video.changesSaved')),
            (this.initialConfig.controls = _.cloneDeep(
              this.localConfig.controls
            )),
            (this.initialConfig.hideControlsOnStart = _.cloneDeep(
              this.localConfig.hideControlsOnStart
            )),
            (this.initialConfig.restartAfterEnd = _.cloneDeep(
              this.localConfig.restartAfterEnd
            )),
            (this.initialConfig.disablePause = _.cloneDeep(
              this.localConfig.disablePause
            )),
            (this.initialConfig.disableForward = _.cloneDeep(
              this.localConfig.disableForward
            )),
            (this.initialConfig.defaultLanguage =
              this.localConfig.defaultLanguage),
            (this.withoutChanges = true))
        this.loadingButtonSave = false
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    /* eslint-disable-next-line */
    async changePlayer(forceReload = false, reloadPlayer = false) {
      reloadPlayer = true
      this.organizeControls()
      this.handlePreviewViaQueryParams()
      /*if (this.mustReloadToPreview || forceReload === true) {
        this.handlePreviewViaQueryParams()
      } else {
        this.handlePreviewViaPostMessage()
      }*/
      this.UPDATE_STATUS_ERROR(false)
      if (reloadPlayer) {
        this.$root.$emit('updateEmbedPreviewChange')
      }
    },
    handlePreviewViaQueryParams() {
      if (this.profile.vsl === null) this.profile.vsl = {}
      this.profile.hideControlsOnStart = this.localConfig.hideControlsOnStart
      this.profile.vsl.defaultLanguage = this.localConfig.defaultLanguage
      this.profile.controls = this.localConfig.controls
      this.profile.vsl.restartAfterEnd = this.localConfig.restartAfterEnd
      this.profile.vsl.disablePause = this.localConfig.disablePause
      this.profile.vsl.disableForward = this.localConfig.disableForward

      this.UPDATE_VIDEO_PROFILE(this.profile)
    },
    handlePreviewViaPostMessage() {
      const iframe = document.querySelector('iframe')
      iframe.contentWindow.postMessage(
        {
          type: 'bulk_update_player_customs',
          parameter: {
            controls: this.localConfig.controls,
            restartAfterEnd: this.localConfig.restartAfterEnd,
            disablePause: this.localConfig.disablePause,
            disableForward: this.localConfig.disableForward,
          },
        },
        '*'
      )
    },
    handleUndoChanges() {
      this.localConfig = _.cloneDeep(this.initialConfig)
      this.changePlayer(true)
    },
    checkChanges() {
      return _.isEqual(this.localConfig, this.initialConfig)
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    organizeControls() {
      const organizedControls = [
        'play-large',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'volume',
        'captions',
        'settings',
        'pip',
        'fullscreen',
        'airplay',
        'cast',
      ]
      this.localConfig.controls.sort(function (a, b) {
        return organizedControls.indexOf(a) - organizedControls.indexOf(b)
      })
      return this.localConfig.controls
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.player-subtitle {
  margin-bottom: 8px;
  font-weight: normal;

  + * {
    padding-top: 6px;
  }
}
</style>
